import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type JSX, useState } from 'react';
import { To } from 'react-router';

import { Button, Link } from 'Components/Buttons/Buttons';
import { ControlTable } from 'Components/ControlTable/ControlTable';
import { Text } from 'Components/Text/Text';
import { ICON_CLOSE, ICON_LINK_OR_MAP, ICON_MOVE_OR_SCROLL_DOWN, ICON_MOVE_OR_SCROLL_UP } from 'Config/Icons';
import { OperationalControl } from 'Models/OperationalControls';

import styles from './AssociatedControlsModal.module.css';
import { Modal } from '../Modal';

const DEFAULT_NUMBER_OF_CONTROLS_SHOWN = 5;

interface AssociatedControlsModalBaseProps {
    hideModal: () => void;
    associatedControls: OperationalControl[];

    /**
     * If undefined, "Controls Mapped" will be the header text, and no secondary text will be used.
     */
    headerText?: { text: string; secondaryText?: string };
}

type AssociatedControlsModalMapControlsProps =
    | {
          /**
           * If defined, a "Map Controls" action is rendered in the footer and navigates to this location.
           */
          mapControlsTo: To;
          onMapControlsClick?: never;
      }
    | {
          mapControlsTo?: never;

          /**
           * If defined, a "Map Controls" action is rendered in the footer and invokes this callback.
           */
          onMapControlsClick: () => void;
      }
    | {
          mapControlsTo?: never;
          onMapControlsClick?: never;
      };

export type AssociatedControlsModalProps = AssociatedControlsModalBaseProps & AssociatedControlsModalMapControlsProps;

/**
 * This is a generic modal used to show the user the controls that are associated with an arbitrary entity. It is intended to be used in "listing" components, where one of the columns shows an "X Controls" button that opens this modal.
 * Renders a `ControlTable` populated with controls, if a non-zero number of controls are provided; otherwise, renders text informing the user that no controls are mapped to the entity.
 *
 * IMPORTANT: This follows the same patterns as `CollapsibleModal`. If a change is made here, consider whether the same change needs to be made there, and vice-versa.
 */
export const AssociatedControlsModal = (props: AssociatedControlsModalProps): JSX.Element => {
    const [showAll, setShowAll] = useState<boolean>(false);

    return (
        <Modal open header={props.headerText?.text ?? 'Controls Mapped'} subHeader={props.headerText?.secondaryText}>
            {props.associatedControls.length > 0 ? (
                <div className={styles.controlTable}>
                    <ControlTable controls={props.associatedControls} limit={showAll ? undefined : DEFAULT_NUMBER_OF_CONTROLS_SHOWN} />
                </div>
            ) : (
                <div>
                    <Text>No controls mapped.</Text>
                </div>
            )}
            <div className={styles.showMoreButton}>
                {props.associatedControls.length > DEFAULT_NUMBER_OF_CONTROLS_SHOWN && (
                    <Button variant="linkText" size="lg" onClick={() => setShowAll(!showAll)}>
                        {showAll ? (
                            <>
                                <FontAwesomeIcon icon={ICON_MOVE_OR_SCROLL_UP} />
                                {` Show Less`}
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={ICON_MOVE_OR_SCROLL_DOWN} />
                                {` Show ${props.associatedControls.length - DEFAULT_NUMBER_OF_CONTROLS_SHOWN} More`}
                            </>
                        )}
                    </Button>
                )}
            </div>
            <div className={'modalFormButtonContainer'}>
                <Button variant="secondary" onClick={props.hideModal} fontAwesomeImage={ICON_CLOSE}>
                    Close
                </Button>
                {props.onMapControlsClick && (
                    <Button variant="primary" onClick={props.onMapControlsClick} fontAwesomeImage={ICON_LINK_OR_MAP}>
                        Map Controls
                    </Button>
                )}
                {!props.onMapControlsClick && props.mapControlsTo && (
                    <Link variant="primaryButton" to={props.mapControlsTo} fontAwesomeImage={ICON_LINK_OR_MAP}>
                        Map Controls
                    </Link>
                )}
            </div>
        </Modal>
    );
};
